import * as React from 'react'
import { Cable, Pencil, Plus, Webhook, Wrench, X } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetClose,
  SheetDescription,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { DeleteToolDialog } from '@/features/tools/components/ToolsAdminPage/DeleteToolDialog'
import { OrganizationToolForm } from '@/features/tools/components/OrganizationToolForm'
import { SigningToolEntry } from '@/features/signing/SigningToolEntry'
import { CalcomToolForm } from '@/features/tools/components/CalcomToolForm'
import { ToolBuilderForm } from '@/features/tools/components/generic/ToolBuilderForm'
import {
  ConnectionForm,
  McpConnectionsPanel,
} from '@/features/tools/McpConnectionsPanel'
import {
  toolDisplayName,
  toolSupportsDavid,
} from '@/features/tools/presentation'
import type {
  AppTool,
  McpConnection,
  McpConnectionTestResult,
} from '@/features/tools/types'
import { getToolIcon } from '@/features/tools/icons'
import {
  useCreateTool,
  useDeleteTool,
  useMcpConnections,
  useOrgTools,
  useUpdateTool,
} from '@/hooks/useToolsAdmin'

function toolHasOrganizationEditor(tool: AppTool): boolean {
  switch (tool.kind) {
    case 'generic_webhook':
    case 'authenticate_caller':
    case 'confirm_appointment':
    case 'get_data':
    case 'transfer_call':
    case 'calcom':
      return true
    case 'configured_duty_handoff':
      return false
    default:
      return false
  }
}

const ToolRow = React.memo(function ToolRow({
  tool,
  onEdit,
  onToggleEnabled,
  isToggling,
}: {
  tool: AppTool
  onEdit: (tool: AppTool) => void
  onToggleEnabled: (tool: AppTool, enabled: boolean) => void
  isToggling: boolean
}) {
  const Icon = getToolIcon(tool.icon)
  const displayName = toolDisplayName(tool)
  return (
    <div className="flex items-center gap-3 rounded-lg border border-neutral-ui-border p-3">
      <Icon className="size-5 shrink-0 text-primary-high-contrast" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-primary-high-contrast">
            {displayName}
          </span>
          {/* Relay permits sharing only Careforce-owned system tools. */}
          {tool.execution_target === 'builtin' || tool.is_system ? (
            <Badge variant="outline">
              {tool.read_only ? 'Shared by Careforce' : 'Managed by Careforce'}
            </Badge>
          ) : null}
          {tool.angelica_enabled ? (
            <Badge variant="outline">Angelica</Badge>
          ) : null}
          {tool.david_enabled && toolSupportsDavid(tool.kind) ? (
            <Badge variant="outline">David</Badge>
          ) : null}
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {tool.description}
        </p>
      </div>
      {!tool.read_only ? (
        <Switch
          checked={tool.enabled}
          disabled={isToggling}
          onCheckedChange={(next) => onToggleEnabled(tool, next === true)}
          aria-label={`${tool.enabled ? 'Disable' : 'Enable'} ${displayName}`}
        />
      ) : null}
      {toolHasOrganizationEditor(tool) && !tool.read_only ? (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Edit ${displayName}`}
          onClick={() => onEdit(tool)}>
          <Pencil className="size-4" />
        </Button>
      ) : null}
    </div>
  )
})

export function ToolsAdminPage() {
  const {
    data: tools,
    isLoading: toolsLoading,
    isError: toolsError,
  } = useOrgTools()
  const {
    data: mcpConnections,
    isLoading: mcpLoading,
    isError: mcpError,
  } = useMcpConnections()
  const createTool = useCreateTool()
  const updateTool = useUpdateTool()
  const deleteTool = useDeleteTool()
  const [editorState, setEditorState] = React.useState<
    | { mode: 'create'; type: 'webhook' | 'mcp' }
    | { mode: 'edit-webhook'; tool: AppTool }
    | {
        mode: 'edit-mcp'
        connection: McpConnection
        initialTestResult?: McpConnectionTestResult
      }
    | null
  >(null)
  const [pendingDelete, setPendingDelete] = React.useState<AppTool | null>(null)
  const [deleteError, setDeleteError] = React.useState<string | null>(null)

  const titleRef = React.useRef<HTMLHeadingElement>(null)
  const EditorIcon =
    editorState?.mode === 'edit-mcp'
      ? Cable
      : getToolIcon(
          editorState?.mode === 'edit-webhook' ? editorState.tool.icon : null
        )
  const close = () => setEditorState(null)
  const editMcpConnection = React.useCallback(
    (connection: McpConnection, initialTestResult?: McpConnectionTestResult) =>
      setEditorState({ mode: 'edit-mcp', connection, initialTestResult }),
    []
  )
  const openEditor = React.useCallback((tool: AppTool) => {
    setEditorState({ mode: 'edit-webhook', tool })
  }, [])
  const { mutateAsync: updateToolAsync } = updateTool
  const hasTools = Boolean(tools?.length || mcpConnections?.length)
  const listLoading = toolsLoading || mcpLoading
  const listError =
    toolsError && mcpError
      ? 'Failed to load tools.'
      : toolsError
        ? 'Webhook tools are unavailable.'
        : mcpError
          ? 'MCP connections are unavailable.'
          : null

  const confirmDelete = async () => {
    if (!pendingDelete) return
    setDeleteError(null)
    try {
      await deleteTool.mutateAsync(pendingDelete.id)
      setPendingDelete(null)
      // The tool is gone -- no point leaving its edit dialog open behind
      // the confirmation once it closes.
      close()
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete tool'
      )
    }
  }

  const toggleEnabled = React.useCallback(
    async (tool: AppTool, enabled: boolean) => {
      try {
        await updateToolAsync({ toolId: tool.id, tool: { enabled } })
        toast.success(
          `${toolDisplayName(tool)} ${enabled ? 'enabled' : 'disabled'}`
        )
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : `Failed to ${enabled ? 'enable' : 'disable'} ${toolDisplayName(tool)}`
        )
      }
    },
    [updateToolAsync]
  )

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench className="size-5" />
          <h1 className="text-lg font-semibold text-primary-high-contrast">
            Tools
          </h1>
        </div>
        <Button
          onClick={() => setEditorState({ mode: 'create', type: 'webhook' })}>
          <Plus className="size-4" />
          New tool
        </Button>
      </div>

      <div data-testid="tools-list" className="flex flex-col gap-2">
        <SigningToolEntry />
        {!hasTools && listLoading ? (
          <p className="text-sm text-muted-foreground">Loading tools…</p>
        ) : null}
        {listError ? (
          <p className="text-sm text-destructive">{listError}</p>
        ) : null}
        {tools
          ?.filter((tool) => String(tool.kind) !== 'signing_documents')
          .map((tool) => (
            <ToolRow
              key={tool.id}
              tool={tool}
              onEdit={openEditor}
              onToggleEnabled={toggleEnabled}
              isToggling={
                updateTool.isPending && updateTool.variables?.toolId === tool.id
              }
            />
          ))}
        <McpConnectionsPanel
          connections={mcpConnections}
          onEdit={editMcpConnection}
        />
      </div>

      <Sheet
        open={editorState !== null}
        onOpenChange={(open) => !open && close()}>
        <SheetContent
          showCloseButton={false}
          className="w-full max-w-none gap-0 overflow-hidden rounded-l-xl p-0 sm:max-w-xl"
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            titleRef.current?.focus({ preventScroll: true })
          }}>
          <SheetHeader className="shrink-0">
            <div className="flex min-w-0 items-center gap-2">
              <EditorIcon aria-hidden="true" className="size-5 shrink-0" />
              <SheetTitle
                ref={titleRef}
                tabIndex={-1}
                className="min-w-0 flex-1 truncate outline-none">
                {editorState?.mode === 'edit-webhook'
                  ? toolDisplayName(editorState.tool)
                  : editorState?.mode === 'edit-mcp'
                    ? editorState.connection.name
                    : 'New tool'}
              </SheetTitle>
              <SheetClose asChild>
                <Button type="button" size="icon-sm" variant="ghost">
                  <X aria-hidden="true" />
                  <span className="sr-only">Close</span>
                </Button>
              </SheetClose>
            </div>
            <SheetDescription className="sr-only">
              {editorState?.mode === 'edit-mcp'
                ? 'Manage sign-in, verification, and agent access.'
                : editorState?.mode === 'edit-webhook' &&
                    editorState.tool.kind === 'confirm_appointment'
                  ? 'Configure shared confirmation instructions for all organizations that use this tool.'
                  : 'Configure this tool for your organization.'}
            </SheetDescription>
            {editorState?.mode === 'create' ? (
              <div className="pt-3">
                <p className="mb-2 text-sm font-medium">Tool type</p>
                <ToggleGroup
                  type="single"
                  variant="outline"
                  value={editorState.type}
                  onValueChange={(type) => {
                    if (type === 'webhook' || type === 'mcp') {
                      setEditorState({ mode: 'create', type })
                    }
                  }}
                  className="w-full">
                  <ToggleGroupItem value="webhook" className="flex-1">
                    <Webhook className="size-4" /> Webhook
                  </ToggleGroupItem>
                  <ToggleGroupItem value="mcp" className="flex-1">
                    <Cable className="size-4" /> MCP server
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            ) : null}
          </SheetHeader>
          {editorState?.mode === 'edit-webhook' &&
          editorState.tool.kind === 'calcom' ? (
            <CalcomToolForm
              key={editorState.tool.id}
              tool={editorState.tool}
              onClose={close}
              onDelete={() => {
                setDeleteError(null)
                setPendingDelete(editorState.tool)
              }}
            />
          ) : editorState?.mode === 'create' && editorState.type === 'mcp' ? (
            <ConnectionForm key="new-mcp" onClose={close} />
          ) : editorState?.mode === 'edit-mcp' ? (
            <ConnectionForm
              key={editorState.connection.id}
              connection={editorState.connection}
              initialTestResult={editorState.initialTestResult}
              onClose={close}
            />
          ) : editorState?.mode === 'edit-webhook' ? (
            <OrganizationToolForm
              tool={editorState.tool}
              onCancel={close}
              onDelete={
                !editorState.tool.is_system
                  ? () => {
                      setDeleteError(null)
                      setPendingDelete(editorState.tool)
                    }
                  : undefined
              }
              onSubmit={async (tool) => {
                await updateTool.mutateAsync({
                  toolId: editorState.tool.id,
                  tool,
                })
                close()
              }}
            />
          ) : editorState?.mode === 'create' &&
            editorState.type === 'webhook' ? (
            <ToolBuilderForm
              onCancel={close}
              onSubmit={async (tool) => {
                if (
                  !tool.description?.trim() ||
                  !tool.args_schema ||
                  !tool.webhook_url
                ) {
                  throw new Error(
                    'Description and webhook configuration are required'
                  )
                }
                await createTool.mutateAsync({
                  ...tool,
                  description: tool.description,
                  args_schema: tool.args_schema,
                  webhook_url: tool.webhook_url,
                  webhook_auth_header: tool.webhook_auth_header ?? null,
                })
                close()
              }}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      <DeleteToolDialog
        tool={pendingDelete}
        isDeleting={deleteTool.isPending}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onOpenChange={(open) => {
          if (deleteTool.isPending) return
          if (!open) {
            setPendingDelete(null)
            setDeleteError(null)
          }
        }}
      />
    </div>
  )
}
